import type { NavigateFunction } from 'react-router-dom';
import { ROUTES } from '../types/global/routes.types';
import { CREATOR_HOME_ROUTE } from '../configurations/creatorDashboardConfig';

export type TDashboardView = 'brand' | 'creator' | 'jury';

const VALID: TDashboardView[] = ['brand', 'creator', 'jury'];

/** Normalizes API value (string or legacy array) to a single dashboard view. */
export function normalizeDashboardView(raw: unknown): TDashboardView {
    if (raw == null || raw === '') return 'creator';
    if (Array.isArray(raw)) return normalizeDashboardView(raw[0]);
    const s = String(raw).toLowerCase().trim();
    if (VALID.includes(s as TDashboardView)) return s as TDashboardView;
    return 'creator';
}

/** Default home route for the user's primary workspace (`dashboard_view`). */
export function getHomeRouteForView(view: TDashboardView): string {
    switch (view) {
        case 'brand':
            return ROUTES.BRAND;
        case 'jury':
            return ROUTES.ESJURY_DUTY;
        case 'creator':
        default:
            return CREATOR_HOME_ROUTE;
    }
}

/** After `dashboard_view` is saved, open the matching workspace route. */
export function navigateToDashboardForView(navigate: NavigateFunction, view: TDashboardView): void {
    navigate(getHomeRouteForView(view));
}

function pathOnly(pathname: string): string {
    const q = pathname.indexOf('?');
    return q === -1 ? pathname : pathname.slice(0, q);
}

/** Main creator-app surfaces: brand/jury users are sent to their workspace instead. */
function isCreatorHubPath(p: string): boolean {
    return p === ROUTES.FEED || p === ROUTES.API_LOADER || p === ROUTES.ESDISCOVERY || p === ROUTES.ESTRENDING;
}

function isJuryHubPath(p: string): boolean {
    return p === ROUTES.ESJURY_DUTY || p.startsWith(`${ROUTES.ESJURY_DUTY}/`);
}

function isBrandWorkspacePath(p: string): boolean {
    return p === ROUTES.BRAND || p.startsWith(`${ROUTES.BRAND}/`);
}

/**
 * Brand users may browse these areas without being forced back to `/brand`.
 * Keep in sync with product (watch, events, settings, etc.).
 */
function isAllowedOutsideBrandWorkspace(p: string): boolean {
    if (isBrandWorkspacePath(p)) return true;
    if (p.startsWith('/settings') || p.startsWith('/account') || p.startsWith('/messages')) return true;
    if (p.startsWith('/watch')) return true;
    if (p.startsWith('/event/') || p.startsWith('/campaign/')) return true;
    if (p.startsWith('/audme') || p.startsWith('/gig')) return true;
    if (p.startsWith('/buzzbridge')) return true;
    if (p.startsWith('/login') || p.startsWith('/signup') || p.startsWith('/forgotPassword')) return true;
    if (p.startsWith('/auth/')) return true;
    return false;
}

/**
 * When signed-in users land on the wrong workspace for their `dashboard_view`, return where to send them.
 * Returns `null` if the current path is OK.
 */
export function getWorkspaceRedirectIfNeeded(pathname: string, view: TDashboardView): string | null {
    const p = pathOnly(pathname);

    if (view === 'brand') {
        if (isAllowedOutsideBrandWorkspace(p)) return null;
        if (isCreatorHubPath(p) || isJuryHubPath(p)) return ROUTES.BRAND;
        return null;
    }

    if (view === 'creator') {
        // if (isBrandWorkspacePath(p)) return CREATOR_HOME_ROUTE;
        return null;
    }

    if (view === 'jury') {
        if (isBrandWorkspacePath(p)) return ROUTES.ESJURY_DUTY;
        if (isCreatorHubPath(p)) return ROUTES.ESJURY_DUTY;
        return null;
    }

    return null;
}
